import { useMutation, useQuery } from '@apollo/client';
import { useCallback, useMemo } from 'react';

import { cacheUtils } from '@eluve/apollo-client';
import { useToast } from '@eluve/components';
import { useTranslation } from '@eluve/i18n';
import { InlineFeedbackBox } from '@eluve/smart-blocks';

import {
  addAppointmentMedicineMutation,
  appointmentMedicinesFragment,
  getAppointmentMedicationsQuery,
  removeAppointmentMedicineMutation,
  updateAppointmentMedicineMutation,
} from './AppointmentMedicine.operations';
import { FHIRMedicineRecommendations } from './FHIRMedicineRecommendations';
import { FHIRMedicationOrder } from './FHIRMedicineRecommendations.types';
import { convertStoredRecommendationsToFHIR } from './utils';

export interface FHIRMedicineRecommendationsContainerProps {
  tenantId: string;
  appointmentId: string;
  isReadonly?: boolean;
}

/**
 * Container component that integrates FHIRMedicineRecommendations with
 * GraphQL data fetching and mutations.
 */
export const FHIRMedicineRecommendationsContainer: React.FC<
  FHIRMedicineRecommendationsContainerProps
> = ({ tenantId, appointmentId, isReadonly }) => {
  const { t } = useTranslation();
  const toast = useToast();

  const { data } = useQuery(getAppointmentMedicationsQuery, {
    variables: {
      tenantId,
      appointmentId,
    },
  });

  const selectedMedicines = data?.appointmentsByPk?.medicines ?? [];
  const rawRecommendations =
    data?.appointmentsByPk?.medicine_recommendations ?? [];

  // Filter out already-selected medicines
  const filteredRawRecommendations = rawRecommendations.filter(
    (m) => !selectedMedicines.find((s) => s.name === m.name),
  );

  // Convert stored recommendations to FHIR format
  const recommendations = useMemo(
    () => convertStoredRecommendationsToFHIR(filteredRawRecommendations),
    [filteredRawRecommendations],
  );

  // Get LLM output ID for feedback
  const llmOutputRecommendationId = rawRecommendations.find(
    (r) => r.recommendationSource === 'LLM Output',
  )?.recommendationSourceId;

  const [addMedication] = useMutation(addAppointmentMedicineMutation, {
    onError: () => toast.error(t('appointmentTools.medicine.addOrderFailed')),
    optimisticResponse: (data) => ({
      insertAppointmentMedicinesOne: {
        __typename: 'AppointmentMedicines' as const,
        id: 'optimistic-id',
        name: data.name,
        ingredients: data.ingredients ?? null,
        externalMedicineId: data.externalMedicineId ?? null,
        appointmentId,
        tenantId,
        externalRawData: data.rawData ?? null,
      },
    }),
    update: (_cache, { data: mutationData }) => {
      cacheUtils.updateFragment(
        {
          fragment: appointmentMedicinesFragment,
          key: { id: appointmentId },
        },
        (existing) => {
          if (!existing || !mutationData?.insertAppointmentMedicinesOne) {
            return existing;
          }
          return {
            ...existing,
            medicines: [
              ...existing.medicines,
              mutationData.insertAppointmentMedicinesOne,
            ],
          };
        },
      );
    },
  });

  const [removeMedication] = useMutation(removeAppointmentMedicineMutation, {
    onError: () =>
      toast.error(t('appointmentTools.medicine.removeMedicationFailed')),
    optimisticResponse: (data) => ({
      deleteAppointmentMedicines: {
        returning: [
          {
            __typename: 'AppointmentMedicines' as const,
            id: data.appointmentMedicineId,
          },
        ],
      },
    }),
    update: (_cache, { data: mutationData }) => {
      cacheUtils.updateFragment(
        {
          fragment: appointmentMedicinesFragment,
          key: { id: appointmentId },
        },
        (existing) => {
          if (!existing || !mutationData?.deleteAppointmentMedicines) {
            return existing;
          }
          return {
            ...existing,
            medicines: [
              ...existing.medicines.filter(
                (m) =>
                  m.id !==
                  mutationData.deleteAppointmentMedicines?.returning?.[0]?.id,
              ),
            ],
          };
        },
      );
    },
  });

  const [updateMedication] = useMutation(updateAppointmentMedicineMutation, {
    onError: () =>
      toast.error(t('appointmentTools.medicine.updateOrderFailed')),
  });

  // Convert existing medicines to FHIRMedicationOrder format for display
  const orders: FHIRMedicationOrder[] = useMemo(
    () =>
      selectedMedicines.map((m) => {
        // Extract order details from externalRawData if available
        const rawData = m.externalRawData as {
          dose?: string;
          frequency?: string;
          route?: string;
          duration?: string;
          quantity?: number;
          refills?: number;
          instructions?: string;
        } | null;

        return {
          id: m.id,
          medication: {
            name: m.name,
            code: m.externalMedicineId ?? undefined,
          },
          dose: rawData?.dose ?? '',
          frequency: rawData?.frequency ?? 'once daily',
          route: rawData?.route ?? 'oral',
          duration: rawData?.duration,
          quantity: rawData?.quantity ?? 30,
          refills: rawData?.refills ?? 0,
          instructions: rawData?.instructions,
        };
      }),
    [selectedMedicines],
  );

  const handleOrderSubmit = useCallback(
    (order: FHIRMedicationOrder) => {
      if (isReadonly) return;

      // Find matching recommendation to get external data
      const recommendation = rawRecommendations.find(
        (r) => r.name === order.medication.name,
      );

      // Build order details to save
      const orderDetails = {
        dose: order.dose,
        frequency: order.frequency,
        route: order.route,
        duration: order.duration,
        quantity: order.quantity,
        refills: order.refills,
        instructions: order.instructions,
      };

      addMedication({
        variables: {
          appointmentId,
          name: order.medication.name,
          ingredients: recommendation?.ingredients ?? null,
          externalMedicineId:
            order.medication.code ?? recommendation?.recommendationSourceId,
          rawData: orderDetails,
        },
      });
    },
    [isReadonly, rawRecommendations, addMedication, appointmentId],
  );

  const handleOrderRemove = useCallback(
    (orderId: string) => {
      if (isReadonly) return;

      removeMedication({
        variables: {
          appointmentId,
          appointmentMedicineId: orderId,
        },
      });
    },
    [isReadonly, removeMedication, appointmentId],
  );

  const handleOrderUpdate = useCallback(
    (order: FHIRMedicationOrder) => {
      if (isReadonly) return;

      const orderDetails = {
        dose: order.dose,
        frequency: order.frequency,
        route: order.route,
        duration: order.duration,
        quantity: order.quantity,
        refills: order.refills,
        instructions: order.instructions,
      };

      updateMedication({
        variables: {
          appointmentId,
          appointmentMedicineId: order.id,
          rawData: orderDetails,
        },
      });
    },
    [isReadonly, updateMedication, appointmentId],
  );

  // Don't render if readonly and no recommendations
  if (isReadonly && recommendations.length === 0 && orders.length === 0) {
    return null;
  }

  return (
    <>
      <FHIRMedicineRecommendations
        tenantId={tenantId}
        appointmentId={appointmentId}
        isReadonly={isReadonly}
        recommendations={recommendations}
        orders={orders}
        onOrderSubmit={handleOrderSubmit}
        onOrderRemove={handleOrderRemove}
        onOrderUpdate={handleOrderUpdate}
      />
      {llmOutputRecommendationId && (
        <InlineFeedbackBox
          appointmentId={appointmentId}
          llmOutputId={llmOutputRecommendationId}
          sectionLabel={t('appointmentTools.medicine.recommendationsFeedback')}
        />
      )}
    </>
  );
};
